/**
 * Phase 20.6.2 — Full-Screen RGM Shuffle Radio Player
 *
 * Pandora/Spotify-style full-screen listening UI living at
 * `/shuffle/radio-player`. Uses the existing PlayerProvider so the
 * underlying queue, audio element, and analytics tracker stay intact —
 * this page is purely a new visual surface.
 *
 * Triggers (set by callers, this page just renders):
 *   • "Start Shuffle Radio" CTA on /shuffle  → navigate here
 *   • Tap on a Shuffle feed card             → navigate here
 *   • Tap the mini-player title when source is shuffle → navigate here
 *
 * Controls: Play/Pause, Next, Previous, Repost, Like, Gift, Comment,
 * Add to Playlist (beat only), Open Full Details. Minimize button
 * navigates(-1) back to wherever the user came from — mini-player keeps
 * playing.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePlayer } from "@/components/player/PlayerProvider";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { optimizeImage } from "@/lib/mediaUrls";

const STATION_LABELS = {
  community: "🔀 Community Shuffle",
  artist:    "🎤 Artist Shuffle",
  producer:  "🎹 Producer Shuffle",
  trending:  "🔥 Trending Shuffle",
  winners:   "🏆 Winners Shuffle",
  following: "👥 Following Shuffle",
  foryou:    "💜 For You Shuffle",
};

const formatTime = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
};

const ControlBtn = ({ onClick, title, testId, size = "md", children, disabled, active }) => {
  const sizing = size === "lg"
    ? "h-16 w-16 text-3xl"
    : size === "sm"
      ? "h-9 w-9 text-base"
      : "h-12 w-12 text-xl";
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      data-testid={testId}
      className={`${sizing} rounded-full flex items-center justify-center transition-all border ${
        active
          ? "bg-pink-500 border-pink-300 text-white shadow-lg shadow-pink-500/40"
          : "bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/40"
      } disabled:opacity-30 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
};

const PrimaryPlayBtn = ({ playing, onClick, disabled, testId }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className="h-20 w-20 rounded-full bg-gradient-to-br from-pink-500 to-fuchsia-500 hover:from-pink-400 hover:to-fuchsia-400 text-white text-4xl flex items-center justify-center shadow-2xl shadow-pink-500/40 disabled:opacity-40 transition-transform hover:scale-105 active:scale-95"
  >
    {playing ? "⏸" : "▶"}
  </button>
);

const ShuffleRadioPlayerPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const {
    track, isPlaying, currentTime, duration, sourceLabel,
    queue, queueIndex, hasNext, hasPrev,
    toggle, next, prev, seekToPercent,
  } = usePlayer();

  const raw = track?.raw || {};
  const isShuffle = !!raw._station;
  const isBeat = raw.kind === "beat";
  const itemId = isBeat ? raw.beat_id : raw.submission_id;

  const [liked, setLiked] = useState(false);
  const [reposted, setReposted] = useState(false);
  const [busy, setBusy] = useState(false);

  // Fetch like + repost state whenever the track changes
  useEffect(() => {
    if (!isShuffle || !itemId) {
      setLiked(false); setReposted(false); return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (isBeat) {
          const [bRes, rRes] = await Promise.all([
            api.get(`/beats/${itemId}`).catch(() => null),
            api.get(`/beats/${itemId}/repost`).catch(() => null),
          ]);
          if (cancelled) return;
          setLiked(!!bRes?.data?.beat?.is_liked_by_me);
          setReposted(!!rRes?.data?.reposted);
        } else {
          const [lRes, rRes] = await Promise.all([
            api.get(`/track/${itemId}/like-status`).catch(() => null),
            api.get(`/track/${itemId}/repost`).catch(() => null),
          ]);
          if (cancelled) return;
          setLiked(!!lRes?.data?.liked);
          setReposted(!!rRes?.data?.reposted);
        }
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [itemId, isBeat, isShuffle]);

  // Empty-state — user landed here without an active radio session
  if (!track || !isShuffle) {
    return (
      <div
        className="min-h-screen bg-[#08080c] text-white flex flex-col items-center justify-center p-8 text-center"
        data-testid="shuffle-radio-player-empty"
      >
        <div className="text-6xl mb-4">🔀</div>
        <h1 className="text-2xl font-black mb-2">No radio playing</h1>
        <p className="text-sm text-gray-400 max-w-sm mb-6">
          Head to RGM Shuffle and hit "Start Shuffle Radio" to fire up a station.
        </p>
        <button
          onClick={() => navigate("/shuffle")}
          data-testid="shuffle-radio-player-empty-cta"
          className="h-12 px-6 rounded-full bg-gradient-to-r from-pink-500 to-fuchsia-500 hover:from-pink-400 hover:to-fuchsia-400 font-black"
        >
          ▶ Start Shuffle Radio
        </button>
      </div>
    );
  }

  const station = raw._station || "community";
  const stationLabel = STATION_LABELS[station] || sourceLabel || "RGM Shuffle";
  const detailHref = isBeat ? `/beats/${itemId}` : `/track/${itemId}`;
  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  const toggleLike = async () => {
    if (busy) return;
    if (!currentUser) { toast.error("Sign in to like"); return; }
    setBusy(true);
    try {
      const url = isBeat ? `/beats/${itemId}/like` : `/track/${itemId}/like`;
      const res = await api.post(url);
      const next_ = !!res.data?.liked;
      setLiked(next_);
      toast.success(next_ ? "❤️ Liked" : "Unliked");
    } catch { toast.error("Could not like"); }
    finally { setBusy(false); }
  };

  const toggleRepost = async () => {
    if (busy) return;
    if (!currentUser) { toast.error("Sign in to repost"); return; }
    setBusy(true);
    try {
      const url = isBeat ? `/beats/${itemId}/repost` : `/track/${itemId}/repost`;
      const res = await api.post(url);
      const next_ = !!res.data?.reposted;
      setReposted(next_);
      toast.success(next_ ? "🔁 Reposted to RGM Shuffle" : "Repost removed");
    } catch { toast.error("Could not repost"); }
    finally { setBusy(false); }
  };

  const openDetail = (anchor = "") => navigate(detailHref + anchor);

  const onSeek = (e) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    seekToPercent(Math.max(0, Math.min(100, pct)));
  };

  const cover = track.coverUrl || raw.cover_url ||
    "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' fill='%23222'/></svg>";

  return (
    <div
      className="min-h-screen text-white relative overflow-hidden flex flex-col"
      data-testid="shuffle-radio-player"
      style={{
        background: `linear-gradient(180deg, rgba(8,8,12,0.4) 0%, rgba(8,8,12,0.85) 50%, #08080c 100%), url(${cover}) center/cover no-repeat fixed`,
      }}
    >
      {/* Frosted veil */}
      <div className="absolute inset-0 backdrop-blur-3xl bg-black/40 pointer-events-none" />

      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Top bar — minimize + station label */}
        <header className="flex items-center justify-between px-4 sm:px-6 pt-5 pb-3 max-w-3xl w-full mx-auto">
          <button
            onClick={() => navigate(-1)}
            data-testid="shuffle-radio-player-minimize"
            title="Minimize"
            className="h-10 w-10 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition-colors flex items-center justify-center text-xl"
          >
            ⌄
          </button>
          <div className="flex flex-col items-center">
            <span className="text-[10px] uppercase tracking-[0.3em] text-purple-200/70 font-bold">
              Playing on
            </span>
            <span
              className="text-xs sm:text-sm font-black text-white"
              data-testid="shuffle-radio-player-station"
            >
              {stationLabel}
            </span>
          </div>
          <button
            onClick={() => openDetail("")}
            data-testid="shuffle-radio-player-open-detail"
            title={isBeat ? "Open beat" : "Open track"}
            className="h-10 w-10 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition-colors flex items-center justify-center text-lg"
          >
            ↗
          </button>
        </header>

        {/* Cover artwork */}
        <div className="flex-1 flex items-center justify-center px-6 sm:px-10">
          <div className="w-full max-w-md aspect-square rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.6)] ring-1 ring-white/10">
            <img
              src={optimizeImage(cover, 'card')}
              alt={track.title || "Cover"}
              data-testid="shuffle-radio-player-cover"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Track info */}
        <div className="px-6 sm:px-10 pb-2 max-w-3xl w-full mx-auto text-center">
          <h1
            className="text-2xl sm:text-3xl font-black truncate"
            data-testid="shuffle-radio-player-title"
            title={track.title}
          >
            {track.title || "Untitled"}
          </h1>
          <p
            className="text-sm sm:text-base text-purple-200/80 mt-1 truncate"
            data-testid="shuffle-radio-player-artist"
          >
            {track.artistName || "Unknown"}
            {raw.kind === "beat" && <span className="text-purple-300/60"> · Producer</span>}
            {raw.kind === "track" && <span className="text-purple-300/60"> · Artist</span>}
          </p>
          {queue.length > 0 && (
            <p className="text-[11px] text-gray-400 mt-1">
              <span data-testid="shuffle-radio-player-queue-pos">
                {Math.max(1, queueIndex + 1)} / {queue.length}
              </span>
            </p>
          )}
        </div>

        {/* Progress */}
        <div className="px-6 sm:px-10 pb-4 max-w-3xl w-full mx-auto">
          <div
            onClick={onSeek}
            data-testid="shuffle-radio-player-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progressPct)}
            className="h-1.5 w-full bg-white/15 rounded-full cursor-pointer group"
          >
            <div
              className="h-full bg-gradient-to-r from-pink-400 to-fuchsia-400 rounded-full relative"
              style={{ width: `${progressPct}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
          <div className="flex justify-between text-[11px] text-gray-400 mt-1 tabular-nums">
            <span data-testid="shuffle-radio-player-time-current">{formatTime(currentTime)}</span>
            <span data-testid="shuffle-radio-player-time-total">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Primary controls */}
        <div className="px-6 pb-3 max-w-3xl w-full mx-auto flex items-center justify-center gap-5">
          <ControlBtn
            onClick={prev}
            title="Previous"
            testId="shuffle-radio-player-prev"
            disabled={!hasPrev}
          >⏮</ControlBtn>
          <PrimaryPlayBtn
            playing={isPlaying}
            onClick={toggle}
            testId="shuffle-radio-player-play"
          />
          <ControlBtn
            onClick={next}
            title="Next"
            testId="shuffle-radio-player-next"
            disabled={!hasNext}
          >⏭</ControlBtn>
        </div>

        {/* Secondary action row */}
        <div className="px-4 pb-8 max-w-3xl w-full mx-auto flex items-center justify-center gap-3 flex-wrap">
          <ControlBtn
            onClick={toggleLike}
            title={liked ? "Unlike" : "Like"}
            testId="shuffle-radio-player-like"
            disabled={busy}
            active={liked}
            size="md"
          >❤</ControlBtn>
          <ControlBtn
            onClick={toggleRepost}
            title={reposted ? "Remove repost" : "Repost"}
            testId="shuffle-radio-player-repost"
            disabled={busy}
            active={reposted}
            size="md"
          >🔁</ControlBtn>
          <ControlBtn
            onClick={() => openDetail("#gift")}
            title="Gift"
            testId="shuffle-radio-player-gift"
            size="md"
          >🎁</ControlBtn>
          <ControlBtn
            onClick={() => openDetail("#comments")}
            title="Comment"
            testId="shuffle-radio-player-comment"
            size="md"
          >💬</ControlBtn>
          {isBeat && (
            <ControlBtn
              onClick={() => openDetail("#playlists")}
              title="Add to playlist"
              testId="shuffle-radio-player-playlist"
              size="md"
            >🎼</ControlBtn>
          )}
          <ControlBtn
            onClick={() => openDetail("")}
            title={isBeat ? "Open beat" : "Open track"}
            testId="shuffle-radio-player-full-detail"
            size="md"
          >🔎</ControlBtn>
        </div>
      </div>
    </div>
  );
};

export default ShuffleRadioPlayerPage;
